import { Box, Paper, TextField, Typography, MenuItem, useTheme } from '@mui/material';
import { BarChart } from '@mui/x-charts';


/**
 * Componente de gráfico de barras para visualização de mensagens por corretor
 * 
 * @component
 * @param {Object} props - Propriedades do componente
 * @param {string[]} props.corretores - Nomes dos corretores
 * @param {number[]} props.mensagens - Número de mensagens por corretor
 * @param {string} props.sortOption - Opção atual de ordenação
 * @param {Function} props.onSortChange - Função de callback para mudança de ordenação
 * 
 * @returns {React.ReactElement} Gráfico de barras com opção de ordenação
 * 
 * @example
 * // Uso básico
 * <CorretorBarChart 
 *   corretores={['João', 'Maria', 'Pedro']} 
 *   mensagens={[10, 25, 15]} 
 *   sortOption="alfabetica"
 *   onSortChange={handleSortChange} 
 * />
 */
export default function CorretorBarChart({
  corretores,
  mensagens,
  sortOption,
  onSortChange,
}) {
  const theme = useTheme();
  const axisTextColor = theme.palette.text.secondary;
  // Calcula o valor máximo para escala do eixo x
  // Usa 1 como mínimo para evitar problemas com array vazio
  const maxValue = Math.max(...mensagens, 1);

  return (
    // Container principal com estilo de papel
    <Paper
      sx={{
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        // Preenchimento interno
        p: 3,
        // Bordas arredondadas
        borderRadius: 2,
        // Sombra sutil para profundidade
        bgcolor: 'background.paper',
        color: 'text.primary',
        boxShadow: theme.palette.mode === 'dark' ? '0 2px 12px rgba(0,0,0,0.28)' : 3,
        border: '1px solid',
        borderColor: 'divider'
      }}
    >
      {/* Cabeçalho do gráfico com opção de ordenação */}
      <Box
        sx={{
          // Layout flexível responsivo
          display: 'flex',
          // Direção de coluna em telas pequenas, linha em telas maiores
          flexDirection: { xs: 'column', sm: 'row' },
          // Distribuição de espaço
          justifyContent: 'space-between',
          // Alinhamento dos itens
          alignItems: { xs: 'flex-start', sm: 'center' },
          // Espaçamento entre elementos responsivo
          gap: { xs: 2, sm: 2, md: 3 },
          // Margem inferior responsiva
          mb: { xs: 2, sm: 3, md: 3 },
        }}
      >
        {/* Título do gráfico com estilo responsivo */}
        <Typography
          variant="h6"
          sx={{
            fontWeight: 'bold',
            color: 'text.primary',
            fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' },
            lineHeight: { xs: 1.3, md: 1.334 },
            // Centralizado em mobile
            textAlign: { xs: 'center', sm: 'left' },
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          Mensagens Enviadas por Corretor
        </Typography>

        {/* Seletor de ordenação com estilo melhorado */}
        <TextField
          // Indica que é um campo de seleção
          select
          // Rótulo do campo
          label="Ordenar por"
          // Valor atual da ordenação
          value={sortOption}
          // Manipulador de mudança de ordenação
          onChange={(e) => onSortChange(e.target.value)}
          // Estilo do campo responsivo
          variant="outlined"
          size="small"
          // Largura responsiva
          sx={{
            minWidth: { xs: '100%', sm: 200 },
            maxWidth: { xs: '100%', sm: 250 },
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              bgcolor: 'background.paper',
              color: 'text.primary',
              '& fieldset': {
                borderColor: 'divider',
              },
              '&:hover fieldset': {
                borderColor: '#667eea',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#667eea',
              },
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: '#667eea',
            },
            '& .MuiInputLabel-root': {
              color: 'text.secondary',
            },
            '& .MuiSelect-icon': {
              color: 'text.secondary',
            },
          }}
        >
          {/* Opções de ordenação */}
          <MenuItem value="alfabetica">Alfabética</MenuItem>
          <MenuItem value="mensagens">Total de Mensagens</MenuItem>
        </TextField>
      </Box>


      {/* Container do gráfico com rolagem horizontal */}
      <Box
        sx={{
          width: '100%',
          maxWidth: '100%',
          mx: 'auto',
          px: { xs: 1, sm: 2, md: 3 },
          overflowX: 'auto',
          overflowY: 'auto',
          maxHeight: '500px', // Limita altura máxima
        }}
      >
        {/* Gráfico de barras horizontal */}
        <BarChart
          height={Math.max(300, corretores.length * 40)}
          // Margens do gráfico
          margin={{ top: 20, left: 125, right: 30, bottom: 40 }}
          // Dados das séries com cor personalizada
          series={[{ data: mensagens, color: '#6c63ff' }]}
          // Layout horizontal
          layout="horizontal"

          // Configurações do eixo x (horizontal)
          xAxis={[{
            // Identificador
            id: 'x-axis',
            // Rótulo do eixo
            label: 'Número de Mensagens',
            // Escala linear para números
            scaleType: 'linear',
            // Estilo dos rótulos
            tickLabelStyle: { fontSize: 14, fill: axisTextColor },
            // Formata valores para números inteiros
            valueFormatter: (value) => Math.round(value).toString(),
            // Incremento mínimo entre marcações
            tickMinStep: 1,
            // Gera valores de marcação de 0 até o valor máximo
            tickValues: Array.from({ length: maxValue + 1 }, (_, i) => i)
          }]}

          // Configurações do eixo y (vertical)
          yAxis={[{
            // Identificador
            id: 'y-axis',
            // Dados dos rótulos (nomes dos corretores)
            data: corretores,
            // Escala de banda para categorias
            scaleType: 'band',
            // Estilo dos rótulos
            tickLabelStyle: { fontSize: 14, fill: axisTextColor },
            // Espaçamento interno entre barras
            paddingInner: 0.7,
            // Espaçamento externo
            paddingOuter: 0.3,
            // Formata rótulos para limitar comprimento
            valueFormatter: (value, context) =>
              context.location === 'tick'
                ? value.split(' ').slice(0, 2).join(' ')
                : value
          }]}

          // Propriedades de slots para personalização
          slotProps={{
            bar: {
              label: {
                // Posição do label
                position: 'right',
                // Estilo do label
                style: { fill: theme.palette.text.primary, fontSize: 14, fontWeight: 'bold' }
              }
            }
          }}

          // Bordas arredondadas
          borderRadius={3}
          // Largura total
          sx={{ width: '100%' }}
        />
      </Box>

      {/* Rodapé com informações adicionais */}
      <Box
        sx={{
          mt: { xs: 2, sm: 3, md: 3 },
          pt: { xs: 2, sm: 2, md: 2 },
          borderTop: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1, sm: 0 },
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
            textAlign: { xs: 'center', sm: 'left' }
          }}
        >
          Total de corretores: {corretores.length}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
            textAlign: { xs: 'center', sm: 'right' }
          }}
        >
          Total de mensagens: {mensagens.reduce((sum, val) => sum + val, 0)}
        </Typography>
      </Box>
    </Paper>
  );
}
